"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { fetchPolymarketMarketMeta } from "../../../api/polymarketApi";
import {
	fetchSupersetCharts,
	fetchSupersetStatus,
} from "../../../api/supersetApi";
import {
	fetchMarket,
	fetchTopLiquidityMarkets,
} from "../../../api/terminalApi";
import type { GroupedMarket } from "../../../types/terminal";
import { groupMarkets } from "../../terminal/selectors/markets";
import { ChartCard } from "../components/ChartCard";
import { EmbedPanel } from "../components/EmbedPanel";
import { MarketPicker } from "../components/MarketPicker";

function formatHeaderDate(iso: string | null): string | null {
	if (!iso) return null;
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

function formatVolume(val: number | null | undefined): string {
	if (!val) return "$0";
	if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
	if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
	return `$${val.toFixed(0)}`;
}

type Mode = "native" | "embed";
type DashboardTab = "overview" | "charts" | "advanced";

const PRELOADED_DEFAULT_MARKET: GroupedMarket = {
	conditionId: "0xb6d6f15a1b5d08753653f1867ccd6126badfbe182a75159a330dc7b15336b309",
	question: "Will Trump create a Strategic Bitcoin Reserve in his first 100 days?",
	closed: false,
	outcomes: ["Yes", "No"],
};

function readModeFromUrl(): Mode {
	if (typeof window === "undefined") return "native";
	return new URLSearchParams(window.location.search).get("mode") === "embed"
		? "embed"
		: "native";
}

function parseDashboardUrl(): {
	marketId: string | null;
	tab: DashboardTab;
	isExplicitMarket: boolean;
} {
	if (typeof window === "undefined") {
		return { marketId: null, tab: "overview", isExplicitMarket: false };
	}
	const pathname = window.location.pathname;
	const match = pathname.match(
		/^\/dashboard\/markets\/([^/]+)(?:\/(charts|advanced))?/,
	);
	if (match) {
		return {
			marketId: match[1] ?? null,
			tab: (match[2] as DashboardTab) || "charts",
			isExplicitMarket: true,
		};
	}
	const searchMarket = new URLSearchParams(window.location.search).get("market");
	if (searchMarket) {
		return {
			marketId: searchMarket,
			tab: "charts",
			isExplicitMarket: true,
		};
	}
	return {
		marketId: null,
		tab: "overview",
		isExplicitMarket: false,
	};
}

function syncDashboardUrl(
	marketId: string | null,
	tab: DashboardTab,
	replace = false,
) {
	if (typeof window === "undefined") return;
	const mode = readModeFromUrl();
	const modeParam = mode === "embed" ? "?mode=embed" : "";
	let nextUrl = "/dashboard";
	if (tab === "overview") {
		nextUrl = `/dashboard${modeParam}`;
	} else if (marketId) {
		nextUrl = `/dashboard/markets/${marketId}/${tab}${modeParam}`;
	} else if (modeParam) {
		nextUrl = `/dashboard${modeParam}`;
	}

	const current = window.location.pathname + window.location.search;
	if (current === nextUrl) return;

	if (replace) {
		window.history.replaceState(null, "", nextUrl);
	} else {
		window.history.pushState(null, "", nextUrl);
	}
}

const EXCLUDED_CHART_IDS = new Set([29, 30, 31]);

// Core charts view: essential, high-signal, fast (4 charts instead of 14)
const CORE_CHART_IDS = [
	37, // 🧬 Edge 1 - Smart-Capital DNA by Side
	38, // ⚡ Edge 2 - Skill-Adjusted Conviction Gap
	36, // 🧠 Smart Score YES vs NO
	35, // ⚖️ Net PnL & Side Summary
];

// Advanced analytics view: in-depth distributions, timing & composition (10 charts)
const ADVANCED_CHART_IDS = [
	43, // 🚨 Suspected Insiders
	41, // 🕵️ Side A — Trader Composition
	42, // 🕵️ Side B — Trader Composition
	50, // 🥧 Side A Ownership Distribution
	51, // 🥧 Side B Ownership Distribution
	52, // 📊 USD-Weighted Wallet Age Distribution
	53, // ⏱️ Holding Duration Distribution
	32, // 📈 Momentum Index 7D
	33, // 🎯 Market Certainty Index
	34, // 🔄 Number of Opinion Changes
];

export function DashboardPage() {
	const [mode] = useState<Mode>(readModeFromUrl);
	const initialInfo = useMemo(parseDashboardUrl, []);
	const [tab, setTab] = useState<DashboardTab>(initialInfo.tab);
	const [market, setMarket] = useState<GroupedMarket>(
		initialInfo.marketId === PRELOADED_DEFAULT_MARKET.conditionId ||
			!initialInfo.marketId
			? PRELOADED_DEFAULT_MARKET
			: {
					conditionId: initialInfo.marketId,
					question: "Loading market…",
					closed: false,
					outcomes: [],
				},
	);
	const [userSearchInput, setUserSearchInput] = useState("");
	const [overviewSearch, setOverviewSearch] = useState("");
	const [loadingChartIds, setLoadingChartIds] = useState<Set<number>>(
		new Set(),
	);

	// Preload selected market details if deep linked
	useEffect(() => {
		if (
			initialInfo.marketId &&
			initialInfo.marketId !== PRELOADED_DEFAULT_MARKET.conditionId
		) {
			void fetchMarket(initialInfo.marketId).then((raw) => {
				if (raw) {
					setMarket({
						conditionId: String(raw.conditionId ?? initialInfo.marketId),
						question: String(raw.question ?? initialInfo.marketId),
						closed: Boolean(raw.closed),
						outcomes: [],
					});
				}
			});
		}
	}, [initialInfo.marketId]);

	// Keep URL synced when browser back/forward buttons are clicked
	useEffect(() => {
		const handlePopState = () => {
			const {
				marketId: popMarketId,
				tab: popTab,
				isExplicitMarket,
			} = parseDashboardUrl();
			setTab(popTab);
			if (popMarketId && popMarketId !== market?.conditionId) {
				void fetchMarket(popMarketId).then((raw) => {
					if (raw) {
						setMarket({
							conditionId: String(raw.conditionId ?? popMarketId),
							question: String(raw.question ?? popMarketId),
							closed: Boolean(raw.closed),
							outcomes: [],
						});
					}
				});
			} else if (!isExplicitMarket) {
				setTab("overview");
			}
		};
		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, [market]);

	const handleSelectMarket = useCallback(
		(next: GroupedMarket, targetTab: DashboardTab = "charts") => {
			setMarket(next);
			setTab(targetTab === "overview" ? "charts" : targetTab);
			syncDashboardUrl(
				next.conditionId,
				targetTab === "overview" ? "charts" : targetTab,
				false,
			);
		},
		[],
	);

	const handleTabChange = useCallback(
		(nextTab: DashboardTab) => {
			setTab(nextTab);
			syncDashboardUrl(market?.conditionId ?? null, nextTab, false);
		},
		[market],
	);

	const handleChartLoadingChange = useCallback(
		(chartId: number, loading: boolean) => {
			setLoadingChartIds((prev) => {
				const isLoading = prev.has(chartId);
				if (loading === isLoading) return prev;
				const next = new Set(prev);
				if (loading) next.add(chartId);
				else next.delete(chartId);
				return next;
			});
		},
		[],
	);

	const handleUserSearchSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			const trimmed = userSearchInput.trim();
			if (trimmed) {
				window.location.href = `/dashboard/users/${trimmed}`;
			}
		},
		[userSearchInput],
	);

	// Load market metadata for the preloaded/active market
	const { data: marketMeta } = useSWR(
		market ? ["polymarket-meta", market.conditionId] : null,
		() => fetchPolymarketMarketMeta(market.conditionId),
		{ revalidateOnFocus: false },
	);

	// Fetch top volume markets for the Market Overview
	const { data: overviewMarketsData, isLoading: isLoadingOverview } = useSWR(
		["overview-markets"],
		async () => {
			const res = await fetchTopLiquidityMarkets(1, 20, false, undefined, "volume");
			return groupMarkets(res.data, { sortBy: "volume" });
		},
		{ revalidateOnFocus: false },
	);

	const overviewMarkets = useMemo(() => {
		const list = overviewMarketsData ?? [];
		if (!overviewSearch.trim()) return list;
		const q = overviewSearch.toLowerCase();
		return list.filter((m) => m.question.toLowerCase().includes(q));
	}, [overviewMarketsData, overviewSearch]);

	const { data: status } = useSWR(
		["superset-status"],
		() => fetchSupersetStatus(),
		{ revalidateOnFocus: false },
	);

	const { data: charts, error: chartsError } = useSWR(
		mode === "native" && status?.configured ? ["superset-charts"] : null,
		() => fetchSupersetCharts("market"),
		{ revalidateOnFocus: false },
	);

	const targetChartIds = tab === "advanced" ? ADVANCED_CHART_IDS : CORE_CHART_IDS;

	const orderedCharts = useMemo(() => {
		if (!charts) return [];
		const targetSet = new Set(targetChartIds);
		const visible = charts.filter(
			(c) => targetSet.has(c.id) && !EXCLUDED_CHART_IDS.has(c.id),
		);
		const priorityIndex = new Map(targetChartIds.map((id, i) => [id, i]));
		return [...visible].sort((a, b) => {
			const pa = priorityIndex.get(a.id) ?? 99;
			const pb = priorityIndex.get(b.id) ?? 99;
			return pa - pb;
		});
	}, [charts, targetChartIds]);

	return (
		<div className="terminal-app">
			<main className="container mx-auto max-w-6xl px-4 py-6">
				{/* Top Header - Clean and Uncluttered */}
				<div className="mb-6">
					<div className="flex items-center justify-between gap-4 flex-wrap">
						<div>
							<h1 className="text-lg md:text-xl font-bold text-base-content font-mono flex items-center gap-2">
								<span className="text-primary">$</span> market analytics
							</h1>
							<p className="text-[11px] text-base-content/50 font-mono mt-1">
								Real-time intelligence driven by Superset, ClickHouse &amp; Metabase
							</p>
						</div>

						{/* Primary View Switcher */}
						<div className="flex items-center gap-2 bg-base-200 p-1 rounded border border-base-content/10 font-mono text-xs">
							<button
								type="button"
								onClick={() => handleTabChange("overview")}
								className={`px-3 py-1.5 rounded transition-colors ${
									tab === "overview"
										? "bg-primary text-primary-content font-bold shadow-sm"
										: "text-base-content/70 hover:text-base-content"
								}`}
							>
								🌐 Market Overview
							</button>
							<button
								type="button"
								onClick={() => handleTabChange("charts")}
								className={`px-3 py-1.5 rounded transition-colors ${
									tab === "charts"
										? "bg-primary text-primary-content font-bold shadow-sm"
										: "text-base-content/70 hover:text-base-content"
								}`}
							>
								📊 Detail Charts ({CORE_CHART_IDS.length})
							</button>
							<button
								type="button"
								onClick={() => handleTabChange("advanced")}
								className={`px-3 py-1.5 rounded transition-colors ${
									tab === "advanced"
										? "bg-primary text-primary-content font-bold shadow-sm"
										: "text-base-content/70 hover:text-base-content"
								}`}
							>
								🔬 Advanced ({ADVANCED_CHART_IDS.length})
							</button>
						</div>
					</div>
				</div>

				{status && !status.configured && (
					<div className="card bg-base-300 border border-warning/30 mb-6">
						<div className="card-body p-4 text-xs font-mono text-warning">
							Superset credentials missing on server. Set SUPERSET_ADMIN_USERNAME / SUPERSET_ADMIN_PASSWORD.
						</div>
					</div>
				)}

				{/* TAB 1: MARKET OVERVIEW (Default View) */}
				{tab === "overview" && (
					<div className="space-y-6">
						{/* Metabase Integration Spotlight Banner */}
						<div className="card bg-gradient-to-r from-base-200 to-base-300 border border-primary/20 shadow-lg">
							<div className="card-body p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
								<div>
									<div className="flex items-center gap-2 text-xs font-bold font-mono text-primary">
										<span>📈</span>
										<span>Metabase Analytics Workspace (dash.polygains.com)</span>
									</div>
									<p className="text-xs font-mono text-base-content/70 mt-1 max-w-2xl">
										Full-scale SQL exploration and cohort dashboards mirror into Metabase with live ClickHouse sync. Explore custom queries or view pre-built trader leaderboards.
									</p>
								</div>
								<a
									href="https://dash.polygains.com"
									target="_blank"
									rel="noopener noreferrer"
									className="btn btn-sm btn-primary font-mono text-xs shrink-0 flex items-center gap-1.5"
								>
									<span>Open Metabase</span>
									<span>↗</span>
								</a>
							</div>
						</div>

						{/* Market Overview Table & Quick Actions */}
						<div className="card bg-base-200 border border-base-content/10">
							<div className="card-body p-4 md:p-5">
								<div className="flex items-center justify-between gap-4 flex-wrap mb-4">
									<div>
										<h2 className="text-sm font-bold font-mono text-base-content">
											🔥 Top Markets by Volume
										</h2>
										<p className="text-[11px] font-mono text-base-content/50">
											Select any market to inspect instant smart-capital DNA and conviction metrics.
										</p>
									</div>
									<input
										type="text"
										value={overviewSearch}
										onChange={(e) => setOverviewSearch(e.target.value)}
										placeholder="Filter markets…"
										className="input input-xs bg-base-300 font-mono text-xs text-base-content border border-base-content/15 rounded w-52"
									/>
								</div>

								{isLoadingOverview && (
									<div className="flex items-center justify-center gap-2 text-xs font-mono text-base-content/50 py-12">
										<span className="inline-block w-4 h-4 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
										<span>Loading top volume markets…</span>
									</div>
								)}

								{!isLoadingOverview && overviewMarkets.length === 0 && (
									<div className="text-xs font-mono text-base-content/40 py-8 text-center">
										No markets found matching "{overviewSearch}".
									</div>
								)}

								{!isLoadingOverview && overviewMarkets.length > 0 && (
									<div className="overflow-x-auto">
										<table className="table table-xs font-mono">
											<thead>
												<tr className="border-b border-base-content/10 text-base-content/50 uppercase text-[10px]">
													<th>Market Question</th>
													<th>24h Volume</th>
													<th>Actions</th>
												</tr>
											</thead>
											<tbody>
												{overviewMarkets.map((m) => {
													const isSelected = m.conditionId === market.conditionId;
													return (
														<tr
															key={m.conditionId}
															className={`border-b border-base-content/5 hover:bg-base-300/50 transition-colors ${
																isSelected ? "bg-primary/5" : ""
															}`}
														>
															<td className="max-w-md py-2.5">
																<div className="font-bold text-xs text-base-content truncate">
																	{m.question}
																</div>
																<div className="text-[10px] text-base-content/40 flex items-center gap-2 mt-0.5">
																	<span>ID: {m.conditionId.slice(0, 10)}…{m.conditionId.slice(-6)}</span>
																	{isSelected && (
																		<span className="badge badge-xs badge-primary">
																			preloaded
																		</span>
																	)}
																</div>
															</td>
															<td className="whitespace-nowrap font-bold text-xs text-primary">
																{formatVolume(m.totalVolume)}
															</td>
															<td className="whitespace-nowrap">
																<div className="flex items-center gap-2">
																	<button
																		type="button"
																		onClick={() => handleSelectMarket(m, "charts")}
																		className="btn btn-xs btn-primary font-mono text-[10px]"
																	>
																		📊 Charts
																	</button>
																	<button
																		type="button"
																		onClick={() => handleSelectMarket(m, "advanced")}
																		className="btn btn-xs btn-ghost font-mono text-[10px] hover:bg-base-300"
																	>
																		🔬 Advanced
																	</button>
																</div>
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								)}
							</div>
						</div>

						{/* Preloaded Selected Market Spotlight */}
						<div className="mt-8 pt-4 border-t border-base-content/10">
							<div className="flex items-center justify-between gap-4 flex-wrap mb-4">
								<div>
									<div className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold">
										Preloaded Selected Market Preview
									</div>
									<h2 className="text-sm md:text-base font-bold text-base-content font-mono mt-0.5">
										{marketMeta?.question || market.question}
									</h2>
								</div>
								<button
									type="button"
									onClick={() => handleTabChange("charts")}
									className="btn btn-sm btn-outline btn-primary font-mono text-xs"
								>
									View Full Detail Page →
								</button>
							</div>

							{mode === "native" && status?.configured && (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{orderedCharts.map((chart) => (
										<ChartCard
											key={chart.id}
											chart={chart}
											marketId={market.conditionId}
											onLoadingChange={handleChartLoadingChange}
										/>
									))}
								</div>
							)}
						</div>
					</div>
				)}

				{/* TAB 2 & 3: DETAIL CHARTS / ADVANCED VIEW */}
				{tab !== "overview" && (
					<div className="space-y-4">
						{/* Market Selector Bar */}
						<div className="flex items-end justify-between gap-4 flex-wrap mb-2">
							<MarketPicker
								selected={market}
								onSelect={(m) => handleSelectMarket(m, tab)}
								initialMarketId={market.conditionId}
							/>
							{loadingChartIds.size > 0 && (
								<div className="flex items-center gap-2 text-[11px] font-mono text-primary/80">
									<span className="inline-block w-3 h-3 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
									Refreshing {loadingChartIds.size} chart
									{loadingChartIds.size === 1 ? "" : "s"}…
								</div>
							)}
						</div>

						{/* Active Market Title Banner */}
						<div className="mb-4 pb-3 border-b border-base-content/10 flex items-start justify-between gap-4">
							<div>
								<h2 className="text-sm md:text-base font-bold text-base-content font-mono">
									{marketMeta?.question || market.question}
								</h2>
								{(formatHeaderDate(marketMeta?.startDate ?? null) ||
									formatHeaderDate(marketMeta?.endDate ?? null)) && (
									<p className="text-[11px] text-base-content/50 font-mono mt-1">
										{formatHeaderDate(marketMeta?.startDate ?? null) ?? "?"} →{" "}
										{formatHeaderDate(marketMeta?.endDate ?? null) ?? "?"}
									</p>
								)}
							</div>
							<button
								type="button"
								onClick={() => handleTabChange("overview")}
								className="text-xs font-mono text-primary hover:underline self-center shrink-0"
							>
								← Back to Overview
							</button>
						</div>

						{/* Native Chart Cards Grid */}
						{mode === "native" && status?.configured && (
							<>
								{chartsError && (
									<div className="text-xs font-mono text-error py-8 text-center">
										Couldn't load the chart list. Please try refreshing.
									</div>
								)}
								{!chartsError && !charts && (
									<div className="flex items-center justify-center gap-2 text-xs font-mono text-base-content/50 py-10">
										<span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
										<span>Loading charts…</span>
									</div>
								)}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{orderedCharts.map((chart) => (
										<ChartCard
											key={chart.id}
											chart={chart}
											marketId={market.conditionId}
											onLoadingChange={handleChartLoadingChange}
										/>
									))}
								</div>
							</>
						)}

						{mode === "embed" && status?.configured && <EmbedPanel />}
					</div>
				)}

				{/* BOTTOM SECTION: SEARCH USER AT THE BOTTOM */}
				<div className="card bg-base-200 border border-base-content/10 mt-12 mb-6">
					<div className="card-body p-4 md:p-5">
						<div className="text-[10px] font-mono uppercase tracking-wider text-base-content/50 mb-1">
							🔍 Search User / Inspect Trader Intelligence
						</div>
						<form
							onSubmit={handleUserSearchSubmit}
							className="flex flex-col md:flex-row gap-2.5 items-stretch md:items-center"
						>
							<input
								type="text"
								value={userSearchInput}
								onChange={(e) => setUserSearchInput(e.target.value)}
								placeholder="Enter 0x... wallet address to inspect trader portfolio & PnL"
								className="input input-sm bg-base-300 font-mono text-xs text-base-content border border-base-content/15 rounded flex-1 max-w-lg focus:outline-primary"
							/>
							<button
								type="submit"
								className="btn btn-sm btn-primary font-mono text-xs shrink-0"
							>
								Inspect Trader ↗
							</button>
						</form>
						<div className="text-[10px] font-mono text-base-content/40 mt-1">
							Try example:{" "}
							<a
								href="/dashboard/users/0x78f329afce2bf9100882e0ee1f83b1e1050c9529"
								className="text-primary hover:underline"
							>
								0x78f329afce2bf9100882e0ee1f83b1e1050c9529
							</a>
						</div>
					</div>
				</div>

				{/* BOTTOM SECTION: NAVIGATION AT THE VERY BOTTOM */}
				<footer className="flex items-center justify-between gap-4 flex-wrap pt-4 pb-8 border-t border-base-content/10 text-xs font-mono text-base-content/60">
					<div className="flex items-center gap-3 flex-wrap">
						<button
							type="button"
							onClick={() => handleTabChange("overview")}
							className="hover:text-primary transition-colors cursor-pointer"
						>
							🌐 Market Overview
						</button>
						<span>•</span>
						<a
							href="/dashboard/users/0x78f329afce2bf9100882e0ee1f83b1e1050c9529"
							className="hover:text-primary transition-colors"
						>
							👤 Trader Intelligence
						</a>
						<span>•</span>
						<a
							href="https://dash.polygains.com"
							target="_blank"
							rel="noopener noreferrer"
							className="hover:text-primary transition-colors flex items-center gap-1"
						>
							<span>📈 Metabase (dash.polygains.com)</span>
							<span>↗</span>
						</a>
					</div>
					<div>
						<a
							href="/"
							className="hover:text-primary transition-colors"
						>
							← Back to Terminal
						</a>
					</div>
				</footer>
			</main>
		</div>
	);
}
export default DashboardPage;
